import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

/** This build's commit, set by the deploy (REACT_APP_BUILD_SHA); empty in local dev, where nothing is checked. */
export const BUILD_VERSION: string = process.env.REACT_APP_BUILD_SHA ?? "";

/** A tab open across a release keeps running the old app (the owner's tab on 13797, 2026-11-01, sat on the old bundle
 * until a hard reload). The build writes version.json with its commit; this asks for it, past the cache, when the tab
 * comes back and every few minutes, and offers a reload once a different version is deployed. */
export default function NewVersionBanner({
  current = BUILD_VERSION,
  intervalMs = 5 * 60_000,
  reload = () => window.location.reload(),
}: {
  current?: string;
  intervalMs?: number;
  reload?: () => void;
}) {
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    if (!current || newer) return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const { version } = (await res.json()) as { version?: string };
        if (!cancelled && version && version !== current) setNewer(true);
      } catch {
        /* offline or mid-deploy: ask again next time */
      }
    };
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    void check();
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    const t = window.setInterval(check, intervalMs);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(t);
    };
  }, [current, intervalMs, newer]);

  if (!newer) return null;
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-3 bg-el-600 px-4 py-2 text-sm text-white"
      data-testid="new-version-banner"
    >
      <span>A new version of Eleetz is ready.</span>
      <button
        type="button"
        onClick={reload}
        className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2.5 py-1 font-semibold hover:bg-white/25"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Reload
      </button>
    </div>
  );
}
